/**
 * marketingSalonSlice — Salon Marketing (Solutrans, etc.).
 *
 * 05/02/2026 (Q1 Lot 2) — Refonte demandée par l'utilisateur :
 *   • « Le salon marketing est trop fréquent et sans impact. »
 *   • Arc narratif dédié avec décisions multiples (budget tiers).
 *   • Résultats variables selon le montant investi.
 *
 * Cycle :
 *   1. **Invitation** : tous les ~210 jours (~7 mois), si une fonction
 *      Marketing est recrutée (Yanis ou Camille) ET pas de salon actif,
 *      un email d'invitation pousse 4 choix budgétaires :
 *         a. Skip          (0 €)              → 0 effet
 *         b. Visiteur      (500 €)            → +2 notoriété
 *         c. Stand standard (3 500 €)         → +5 notoriété +2 réputation +1 prospect AO
 *         d. Stand premium (9 000 €)          → +10 notoriété +5 réputation +2 prospects AO
 *   2. **Acceptation** (`attendMarketingSalon`) : débite la trésorerie,
 *      pose un `activeSalon = { tier, attendedDay, resultDay = today + 7 }`.
 *   3. **Salon en cours** (jours 1-6) : message d'ambiance discret.
 *   4. **Bilan** (jour 7) : `tickMarketingSalon` applique notoriété/réputation,
 *      injecte les prospects AO bonus (si tier ≥ standard) dans
 *      `state.salonProspectTenders` consommé par `getAvailableTenders`,
 *      pousse un email de bilan signé Yanis / Camille / "Service marketing".
 *
 * Le state `state.marketingSalon` est null tant qu'aucun salon n'est en
 * cours. Le compteur `lastSalonProposalDay` régule la cadence d'invitation.
 */

const SALON_INVITATION_INTERVAL_DAYS = 210; // ~7 mois
const SALON_RESULT_DELAY_DAYS = 7;          // durée du salon (J+7 = bilan)
const SALON_INVITATION_PROBABILITY = 0.85;  // proba à chaque fenêtre éligible

export const SALON_TIERS = {
  visit:    { cost: 500,   notoriety: 2,  reputation: 0, prospects: 0, label: 'Visiteur' },
  standard: { cost: 3500,  notoriety: 5,  reputation: 2, prospects: 1, label: 'Stand standard' },
  premium:  { cost: 9000,  notoriety: 10, reputation: 5, prospects: 2, label: 'Stand premium' },
};

const SALON_EVENTS = [
  { id: 'solutrans', name: 'Solutrans Lyon', city: 'Lyon' },
  { id: 'transport-paris', name: 'Mondial Transport Paris', city: 'Paris' },
  { id: 'logistique-toulouse', name: 'SITL Toulouse', city: 'Toulouse' },
  { id: 'iaa-hannover', name: 'IAA Transportation Hannover', city: 'Hanovre' },
];

function pickSalonEvent(rng = Math.random) {
  return SALON_EVENTS[Math.floor(rng() * SALON_EVENTS.length)];
}

function hasMarketingSupport(state) {
  const hired = state.hiredSupport || {};
  return !!(hired['role-yanis-elkaroui'] || hired['role-sherazade-fetitou']);
}

function pickMarketingSigner(state) {
  const hired = state.hiredSupport || {};
  if (hired['role-sherazade-fetitou']) {
    return { name: 'Sherazade Fetitou', title: 'Responsable marketing senior' };
  }
  if (hired['role-yanis-elkaroui']) {
    return { name: 'Yanis Elkaroui', title: 'Marketing junior' };
  }
  return { name: 'Service marketing', title: 'Communication' };
}

export function createMarketingSalonSlice(set, get) {
  return {
    // State
    marketingSalon: null,            // { eventId, eventName, tier, attendedDay, resultDay } | null
    lastSalonProposalDay: 0,         // last day an invitation was rolled
    salonProspectTenders: 0,         // count of bonus AO prospects unlocked (consumed on next salon)

    /**
     * Accept the salon invitation at a given budget tier.
     * @param {'skip'|'visit'|'standard'|'premium'} tier
     * @returns {{ ok: boolean, reason?: string }}
     */
    attendMarketingSalon: (tier) => {
      const state = get();
      if (state.marketingSalon && state.marketingSalon.active) {
        return { ok: false, reason: 'salon_already_active' };
      }
      if (tier === 'skip') {
        // No state mutation, just consume the invitation cycle.
        set({ lastSalonProposalDay: state.totalDays || 0 });
        return { ok: true };
      }
      const tierData = SALON_TIERS[tier];
      if (!tierData) return { ok: false, reason: 'invalid_tier' };
      const treasury = state.treasury || 0;
      if (treasury < tierData.cost) return { ok: false, reason: 'insufficient_treasury' };

      const today = state.totalDays || 0;
      const evt = pickSalonEvent();
      set({
        treasury: treasury - tierData.cost,
        marketingSalon: {
          active: true,
          eventId: evt.id,
          eventName: evt.name,
          city: evt.city,
          tier,
          tierCost: tierData.cost,
          attendedDay: today,
          resultDay: today + SALON_RESULT_DELAY_DAYS,
        },
        lastSalonProposalDay: today,
      });

      // Push confirmation email.
      const pushMsg = get().pushCustomMessage;
      if (typeof pushMsg === 'function') {
        const signer = pickMarketingSigner(state);
        try {
          pushMsg({
            senderType: 'advisor',
            senderName: signer.name,
            tone: 'friendly',
            kind: 'info',
            subject: `Inscription confirmée — ${evt.name}`,
            body:
              `Notre dossier salon **${evt.name}** est confirmé en **${tierData.label}**.\n\n` +
              `• Investissement : ${tierData.cost.toLocaleString('fr-FR')} € (débité)\n` +
              `• Ouverture : ${evt.city}, dans 5 jours\n` +
              `• Bilan attendu : sous ${SALON_RESULT_DELAY_DAYS} jours\n\n` +
              `Je reviens vers vous dès la clôture avec les retombées (notoriété, prospects, partenariats).\n\n` +
              `— ${signer.name}, ${signer.title}`,
            ttlDays: SALON_RESULT_DELAY_DAYS + 4,
          });
        } catch (err) { console.warn('[salon] confirm push failed:', err); }
      }
      return { ok: true };
    },

    /**
     * Daily tick — called from coreSlice.nextDay.
     *   1. Resolves an active salon when resultDay is reached.
     *   2. Otherwise, every ~210 days if eligible, rolls a new invitation.
     */
    tickMarketingSalon: () => {
      const state = get();
      const today = state.totalDays || 0;
      const active = state.marketingSalon;

      // 1) Resolve active salon at result day.
      if (active && active.active && today >= active.resultDay) {
        const tierData = SALON_TIERS[active.tier];
        if (!tierData) {
          set({ marketingSalon: null });
          return { resolved: false, proposal: false };
        }
        const newNotoriety = Math.max(0, Math.min(100, (state.notoriety || 0) + tierData.notoriety));
        const newReputation = Math.max(0, Math.min(100, (state.reputation || 50) + tierData.reputation));
        const addedProspects = tierData.prospects || 0;

        set({
          marketingSalon: null,
          notoriety: newNotoriety,
          reputation: newReputation,
          salonProspectTenders: (state.salonProspectTenders || 0) + addedProspects,
        });

        const pushMsg = get().pushCustomMessage;
        if (typeof pushMsg === 'function') {
          const signer = pickMarketingSigner(state);
          try {
            pushMsg({
              senderType: 'advisor',
              senderName: signer.name,
              tone: 'friendly',
              kind: 'info',
              subject: `Bilan salon — ${active.eventName}`,
              body:
                `**Salon ${active.eventName} clôturé.**\n\n` +
                `Tier choisi : **${tierData.label}** (${active.tierCost.toLocaleString('fr-FR')} €)\n\n` +
                `Retombées :\n` +
                `• Notoriété : **+${tierData.notoriety}** points\n` +
                (tierData.reputation > 0 ? `• Réputation : **+${tierData.reputation}** points\n` : '') +
                (addedProspects > 0
                  ? `• **${addedProspects} prospect(s) AO** débloqué(s) sur les prochains appels d'offres.\n`
                  : `• Aucun prospect AO direct (à ce tier, on garde un profil bas — passer au stand standard ou premium ouvre les portes).\n`) +
                `\nMerci à toute l'équipe pour la tenue du stand.\n\n` +
                `— ${signer.name}, ${signer.title}`,
              ttlDays: 14,
            });
          } catch (err) { console.warn('[salon] bilan push failed:', err); }
        }
        return { resolved: true, proposal: false };
      }

      // 2) Roll a new invitation if eligible.
      const lastTry = state.lastSalonProposalDay || 0;
      if (active) return { resolved: false, proposal: false }; // wait for active resolution
      if (today - lastTry < SALON_INVITATION_INTERVAL_DAYS) return { resolved: false, proposal: false };
      if (!hasMarketingSupport(state)) {
        // Stamp last try so we don't spam-check daily once eligibility resumes.
        set({ lastSalonProposalDay: today });
        return { resolved: false, proposal: false };
      }
      if (Math.random() > SALON_INVITATION_PROBABILITY) {
        // Skip this window; retry in 30 days.
        set({ lastSalonProposalDay: today - (SALON_INVITATION_INTERVAL_DAYS - 30) });
        return { resolved: false, proposal: false };
      }

      // Push invitation as a decision message (custom kind handled by UI).
      const evt = pickSalonEvent();
      set({ lastSalonProposalDay: today });

      const pushMsg = get().pushCustomMessage;
      if (typeof pushMsg === 'function') {
        const signer = pickMarketingSigner(state);
        try {
          pushMsg({
            senderType: 'advisor',
            senderName: signer.name,
            tone: 'friendly',
            kind: 'info', // info — the actual decision happens in dedicated UI button (slice action)
            subject: `Salon ${evt.name} — invitation`,
            body:
              `Le salon **${evt.name}** ouvre ses portes dans 30 jours à ${evt.city}.\n\n` +
              `Trois niveaux d'engagement possibles :\n` +
              `• **Visiteur** (${SALON_TIERS.visit.cost.toLocaleString('fr-FR')} €) — présence discrète, +${SALON_TIERS.visit.notoriety} notoriété\n` +
              `• **Stand standard** (${SALON_TIERS.standard.cost.toLocaleString('fr-FR')} €) — visibilité moyenne, +${SALON_TIERS.standard.notoriety} notoriété, +${SALON_TIERS.standard.reputation} réputation, +${SALON_TIERS.standard.prospects} prospect AO\n` +
              `• **Stand premium** (${SALON_TIERS.premium.cost.toLocaleString('fr-FR')} €) — show de force, +${SALON_TIERS.premium.notoriety} notoriété, +${SALON_TIERS.premium.reputation} réputation, +${SALON_TIERS.premium.prospects} prospects AO\n\n` +
              `Je peux engager le dossier sur un de ces trois niveaux (ou passer notre tour) — décidez ` +
              `dans la **section Salon Marketing** du Dashboard.\n\n` +
              `— ${signer.name}, ${signer.title}`,
            ttlDays: 21,
          });
        } catch (err) { console.warn('[salon] invite push failed:', err); }
      }

      // Stash the pending invitation in state so UI can render the action panel.
      set({
        pendingSalonInvitation: {
          active: true,
          eventId: evt.id,
          eventName: evt.name,
          city: evt.city,
          offeredOnDay: today,
          expiresOnDay: today + 21,
        },
      });

      return { resolved: false, proposal: true };
    },

    /**
     * Dismiss a pending invitation without booking.
     */
    declineMarketingSalon: () => {
      const state = get();
      const today = state.totalDays || 0;
      set({
        pendingSalonInvitation: null,
        lastSalonProposalDay: today,
      });
      return { ok: true };
    },
  };
}

export const _SALON_INTERNAL = {
  SALON_TIERS,
  SALON_EVENTS,
  SALON_INVITATION_INTERVAL_DAYS,
  SALON_RESULT_DELAY_DAYS,
};
